import { useEffect, useRef, useState, type ChangeEvent, type DragEvent, type ReactNode } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { api } from '@aeon-karaoke/cloud-convex';
import type { Id } from '@aeon-karaoke/cloud-convex/_generated/dataModel';
import { pipelineStages, type Stage } from '@aeon-karaoke/cloud-convex/model/pipelineStages';
import { PLANS } from '@aeon-karaoke/cloud-convex/model/plans';
import { trackState } from '@aeon-karaoke/cloud-convex/model/trackState';
import { Button, Input, LoadingState, MonoLabel, StripePlaceholder, buttonClassName } from '@/component';
import { useMyTracksUsage, useTrack, useTrackJob, useTrackUpload, type TrackView, type UploadIntent } from '@/hook';
import { AUDIO_ACCEPT, draftFromFileName, humanSize, inspectAudioFile } from '@/lib/audioFile';
import { cn } from '@/lib/cn';
import { convex } from '@/lib/convex';
import { toResult } from '@/lib/result-adapter';
import { formatDuration } from '@/lib/time';
import { CreatePipeline } from './CreatePipeline';

const STEPS = [1, 2, 3] as const;

const formatReset = (ms: number) =>
  new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', timeZone: 'UTC' });

/** The file chosen on this page plus the intent the server issued for it. */
interface PendingUpload extends UploadIntent {
  file: File;
}

/** /create: pick a file, name it, watch it build. With `?track=<id>` the page
 * resumes that track wherever it is: naming for a draft, progress, failed or
 * finished for anything submitted. */
export function CreatePage({ trackId }: { trackId?: Id<'tracks'> }) {
  if (trackId) return <TrackRoute trackId={trackId} />;
  return <CreateFlow />;
}

function CreateHeader({ step, title, blurb, children }: { step: 1 | 2 | 3; title: string; blurb: string; children: ReactNode }) {
  return (
    <div className="max-w-[720px] pt-[52px]">
      <MonoLabel tone="mute" size="md" className="block">
        Step {step} of 3
      </MonoLabel>
      <h1 className="mt-[14px] text-[44px] font-semibold leading-[1.04] tracking-[-0.03em]">{title}</h1>
      <p className="mt-3 max-w-[52ch] text-base leading-[1.55] text-mute">{blurb}</p>
      <div className="mt-[34px] flex gap-2" aria-hidden="true">
        {STEPS.map((n) => (
          <div
            key={n}
            className={cn(
              'h-[3px] flex-1 rounded-sm transition-colors duration-300 motion-reduce:transition-none',
              step >= n ? 'bg-accFill' : 'bg-line'
            )}
          />
        ))}
      </div>
      <div className="mt-[34px]">{children}</div>
    </div>
  );
}

/** Creates the draft the moment a file is chosen, so the limit is checked and
 * the key is ours before the PUT starts. */
function CreateFlow() {
  const usage = useMyTracksUsage();
  const fileInput = useRef<HTMLInputElement>(null);
  // Counts selections so a slow inspection of an earlier file cannot land
  // after the user has picked another, and runs picks one after another so a
  // stale draft is gone before the next one asks for the month's slot.
  const selection = useRef(0);
  const queue = useRef<Promise<void>>(Promise.resolve());
  const [pending, setPending] = useState<PendingUpload | null>(null);
  const track = useTrack(pending?.trackId);
  const [dragging, setDragging] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  function choose(file: File) {
    const mine = ++selection.current;
    setFileError(null);
    const run = queue.current.then(() => chooseNow(file, mine));
    queue.current = run.catch(() => undefined);
    return run;
  }

  async function chooseNow(file: File, mine: number) {
    const check = await inspectAudioFile(file);
    if (mine !== selection.current) return;
    if (!check.ok) return setFileError(check.message);
    const intent = await toResult(() =>
      convex.mutation(api.tracks.createDraft, {
        fileName: file.name,
        bytes: file.size,
        contentType: file.type || undefined,
        durationMs: check.durationMs,
      })
    );
    if (mine !== selection.current) {
      // A newer pick won the race; this draft must not keep the month's slot.
      if (intent.ok) await convex.mutation(api.tracks.remove, { trackId: intent.value.trackId });
      return;
    }
    if (!intent.ok) return setFileError(intent.error.message);
    setPending({ ...intent.value, file });
  }

  function onPick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) void choose(file);
  }

  function onDrop(event: DragEvent) {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) void choose(file);
  }

  if (usage === undefined) return <LoadingState />;

  if (pending) {
    if (track === undefined) return <LoadingState />;
    return <NameStep key={track._id} track={track} pending={pending} onDiscarded={() => setPending(null)} />;
  }

  if (usage.atLimit) {
    const limit = usage.limit ?? 0;
    return (
      <CreateHeader
        step={1}
        title="Create a karaoke track"
        blurb="Drop in an audio file. We separate the vocal, build the instrumental, and time the lyrics. The finished Aeon track is yours."
      >
        <div className="rounded-lg border border-down bg-surface p-7">
          <MonoLabel tone="down" size="md" className="block">
            Monthly limit reached
          </MonoLabel>
          <p className="mt-3 max-w-[40ch] text-[19px] font-medium leading-[1.4] tracking-[-0.02em]">
            You have used all {limit} tracks on {PLANS[usage.tier].name} this month.
          </p>
          <p className="mt-[10px] max-w-[48ch] text-[15px] leading-[1.55] text-mute">
            Your next track unlocks on {formatReset(usage.resetsAt)}.
            {usage.tier === 'free' && ` ${PLANS.beta.name} lifts this to ${PLANS.beta.monthlyCreateLimit} a month.`}
          </p>
          <div className="mt-[22px] flex flex-wrap gap-[10px]">
            <Link to="/settings/$tab" params={{ tab: 'plan' }} className={buttonClassName({ className: 'px-[19px]' })}>
              See plans
            </Link>
            <Link to="/library" className={buttonClassName({ variant: 'outline', className: 'px-[17px]' })}>
              Not now
            </Link>
          </div>
        </div>
      </CreateHeader>
    );
  }

  return (
    <CreateHeader
      step={1}
      title="Create a karaoke track"
      blurb="Drop in an audio file. We separate the vocal, build the instrumental, and time the lyrics. The finished Aeon track is yours."
    >
      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn('rounded-lg border border-dashed px-8 py-14 text-center', dragging ? 'border-accFill' : 'border-line2')}
      >
        <div className="text-lg font-semibold tracking-[-0.015em]">Drop an audio file here</div>
        <p className="mt-[9px] text-sm text-mute">WAV, FLAC, MP3 or M4A. Up to 12 minutes, 200 MB.</p>
        <Button onClick={() => fileInput.current?.click()} className="mt-5 px-[19px]">
          Choose a file
        </Button>
        <input ref={fileInput} type="file" accept={AUDIO_ACCEPT} onChange={onPick} className="hidden" data-testid="audio-input" />
      </div>
      {fileError && <p className="mt-3 text-[13px] text-down">{fileError}</p>}
      <div className="mt-6 flex items-baseline justify-between gap-5 border-b border-stripeA border-t border-line py-[14px]">
        <MonoLabel>This month</MonoLabel>
        <span className="text-sm font-medium">
          {usage.limit === null
            ? `${usage.used} created · unlimited on ${PLANS[usage.tier].name}`
            : `${usage.used} of ${usage.limit} created · resets ${formatReset(usage.resetsAt)}`}
        </span>
      </div>
      <p className="mt-[18px] max-w-[60ch] text-[13px] leading-[1.6] text-faint">
        Upload only music you have the right to use. We separate the vocal, build the instrumental, and time the lyrics.
      </p>
    </CreateHeader>
  );
}

/** Step 2. Runs the PUT for a fresh pick while the user names the track, or
 * resumes a draft that already landed. A draft whose upload never finished
 * offers the file again through replaceSource. */
function NameStep({ track, pending, onDiscarded }: { track: TrackView; pending: PendingUpload | null; onDiscarded: () => void }) {
  const navigate = useNavigate();
  const { upload, abort, progress } = useTrackUpload();
  const retryInput = useRef<HTMLInputElement>(null);
  const started = useRef(false);
  const [title, setTitle] = useState(() => (pending ? draftFromFileName(pending.file.name).title : track.title));
  const [artist, setArtist] = useState(() => (pending ? draftFromFileName(pending.file.name).artist : (track.artist ?? '')));
  const [share, setShare] = useState(track.allowSessionQueue ?? true);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send(file: File, intent: UploadIntent) {
    setUploadError(null);
    const result = await upload(file, intent);
    if (!result.ok) setUploadError(result.error.message);
  }

  useEffect(() => {
    if (!pending || started.current) return;
    started.current = true;
    void send(pending.file, pending);
    // The PUT starts once per pick; the ref guards re-renders.
  }, [pending]);

  async function chooseAgain(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const check = await inspectAudioFile(file);
    if (!check.ok) return setUploadError(check.message);
    const intent = await toResult(() =>
      convex.mutation(api.tracks.replaceSource, {
        trackId: track._id,
        fileName: file.name,
        bytes: file.size,
        contentType: file.type || undefined,
        durationMs: check.durationMs,
      })
    );
    if (!intent.ok) return setUploadError(intent.error.message);
    await send(file, intent.value);
  }

  async function submit() {
    setBusy(true);
    setError(null);
    const result = await toResult(() =>
      convex.mutation(api.tracks.submit, {
        trackId: track._id,
        title: title.trim(),
        artist: artist.trim() || null,
        allowSessionQueue: share,
      })
    );
    setBusy(false);
    if (result.ok) void navigate({ to: '/create', search: { track: track._id }, replace: true });
    else setError(result.error.message);
  }

  /** Back throws the draft away for good, so the monthly slot comes back. A
   * PUT still in flight is stopped first so it cannot land after the row. */
  async function discard() {
    setBusy(true);
    abort();
    const result = await toResult(() => convex.mutation(api.tracks.remove, { trackId: track._id }));
    setBusy(false);
    if (!result.ok) return setError(result.error.message);
    onDiscarded();
  }

  const received = track.upload?.received ?? false;
  const stalled = !pending && !received && progress === null;
  const chip = uploadError || stalled
    ? { label: 'Failed', tone: 'down' as const }
    : received
      ? { label: 'Received', tone: 'up' as const }
      : { label: progress === null ? 'Uploading' : `Uploading ${Math.round(progress * 100)}%`, tone: 'acc' as const };
  const bytes = pending?.file.size ?? track.upload?.bytes ?? null;
  const fileMeta = [
    bytes !== null ? humanSize(bytes) : null,
    track.durationMs !== undefined ? formatDuration(track.durationMs) : null,
    received ? 'uploaded' : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <CreateHeader step={2} title="Name the track" blurb="Confirm what we should call it. You can edit this later from your library.">
      <div className="flex flex-col gap-[22px]">
        <div className="flex items-center gap-4 rounded-md border border-line bg-surface p-4">
          <div className="h-11 w-11 shrink-0 rounded bg-stripes" />
          <div className="min-w-0 flex-1">
            <div className="truncate font-mono text-[13px]">{pending?.file.name ?? track.upload?.fileName ?? track.sourceFileName}</div>
            <div className="mt-[3px] text-[13px] text-mute">
              {uploadError ?? (stalled ? 'The upload did not finish.' : fileMeta)}
            </div>
          </div>
          <MonoLabel tone={chip.tone} className="label-fade shrink-0" key={chip.label}>
            {chip.label}
          </MonoLabel>
        </div>
        {(uploadError || stalled) && (
          <div>
            <Button variant="outline" onClick={() => retryInput.current?.click()} className="px-[17px]">
              Choose the file again
            </Button>
            <input ref={retryInput} type="file" accept={AUDIO_ACCEPT} onChange={(event) => void chooseAgain(event)} className="hidden" />
          </div>
        )}
        <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-[18px]">
          <Input label="Song title" value={title} onChange={(event) => setTitle(event.target.value)} />
          <Input label="Artist" value={artist} onChange={(event) => setArtist(event.target.value)} />
        </div>
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={share}
            onChange={(event) => setShare(event.target.checked)}
            className="mt-[2px] h-[17px] w-[17px] accent-accFill"
          />
          <span className="text-sm leading-[1.5]">
            Let friends queue this track in sessions I am in
            <span className="mt-[2px] block text-mute">You can change this per track later.</span>
          </span>
        </label>
        {error && <p className="text-[13px] text-down">{error}</p>}
        <div className="flex gap-[10px] pt-1">
          <Button onClick={() => void submit()} disabled={busy || !received || !title.trim()} className="px-[19px]">
            Start transforming
          </Button>
          <Button variant="outline" disabled={busy} onClick={() => void discard()} className="px-[17px]">
            Back
          </Button>
        </div>
      </div>
    </CreateHeader>
  );
}

function TrackRoute({ trackId }: { trackId: Id<'tracks'> }) {
  const navigate = useNavigate();
  const track = useTrack(trackId);
  if (track === undefined) return <LoadingState />;
  if (track.processingStatus === 'uploading' || track.processingStatus === 'draft') {
    return <NameStep key={track._id} track={track} pending={null} onDiscarded={() => void navigate({ to: '/create', replace: true })} />;
  }
  return <TrackBuild trackId={trackId} />;
}

const FAILED_COPY = {
  received: {
    headline: 'We could not read this audio file.',
    cause: 'This usually means the file is damaged or not really audio.',
  },
  separated: {
    headline: 'We could not separate the vocal on this recording.',
    cause: 'This usually means the recording is live, very quiet, or already an instrumental.',
  },
  lyrics: {
    headline: 'We could not time the lyrics on this recording.',
    cause: 'This usually means the vocal is hard to make out, or the song has no words.',
  },
  mastered: {
    headline: 'We could not finish mastering this track.',
    cause: 'This is usually a hiccup on our side.',
  },
} satisfies Record<Stage['key'], { headline: string; cause: string }>;

function TrackBuild({ trackId }: { trackId: Id<'tracks'> }) {
  const navigate = useNavigate();
  const track = useTrack(trackId);
  const job = useTrackJob(trackId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (track === undefined || job === undefined) return <LoadingState />;

  const state = trackState(track.processingStatus);
  const { stages, percent } = pipelineStages({
    processingStatus: track.processingStatus,
    completedSteps: job?.completedSteps ?? [],
  });
  const name = track.artist ? `${track.title} · ${track.artist}` : track.title;

  async function retry() {
    setBusy(true);
    setError(null);
    const result = await toResult(() => convex.mutation(api.tracks.retry, { trackId }));
    setBusy(false);
    if (!result.ok) setError(result.error.message);
  }

  if (state === 'ready') {
    return (
      <CreateHeader step={3} title="Ready to sing" blurb="Queue it on any Aeon system, or make another.">
        <div className="flex flex-wrap items-center gap-[22px] rounded-lg border border-line bg-surface p-7">
          <StripePlaceholder className="h-[92px] w-[92px] shrink-0 rounded-md" />
          <div className="min-w-[220px] flex-1">
            <MonoLabel tone="up" size="md" className="block">
              Ready to sing
            </MonoLabel>
            <div className="mt-2 text-2xl font-semibold tracking-[-0.025em]">{track.title}</div>
            <div className="mt-[2px] text-[15px] text-mute">
              {[track.artist, track.durationMs !== undefined ? formatDuration(track.durationMs) : null].filter(Boolean).join(' · ')}
            </div>
          </div>
          <div className="flex flex-wrap gap-[10px]">
            <Link to="/tracks/$trackId" params={{ trackId }} className={buttonClassName({ className: 'px-[19px]' })}>
              Open track
            </Link>
            <Link to="/create" className={buttonClassName({ variant: 'outline', className: 'px-[17px]' })}>
              Create another
            </Link>
          </div>
        </div>
      </CreateHeader>
    );
  }

  if (state === 'attention') {
    const failedKey = stages.find((s) => s.state === 'Failed')?.key ?? 'mastered';
    const copy = FAILED_COPY[failedKey];
    return (
      <CreateHeader step={3} title="Track not finished" blurb="Something went wrong on the way. Nothing was charged against your monthly count.">
        <div className="rounded-lg border border-down bg-surface p-7">
          <MonoLabel tone="down" size="md" className="block">
            Needs attention
          </MonoLabel>
          <p className="mt-3 max-w-[44ch] text-[19px] font-medium leading-[1.4] tracking-[-0.02em]">{copy.headline}</p>
          <p className="mt-[10px] max-w-[52ch] text-[15px] leading-[1.55] text-mute">
            {copy.cause} Nothing was charged against your monthly count.
          </p>
          <div className="mt-[22px]">
            <CreatePipeline stages={stages} compact />
          </div>
          {error && <p className="mt-4 text-[13px] text-down">{error}</p>}
          <div className="mt-6 flex flex-wrap gap-[10px]">
            <Button onClick={() => void retry()} disabled={busy} className="px-[19px]">
              Try again
            </Button>
            <Button variant="outline" disabled={busy} onClick={() => void navigate({ to: '/create' })} className="px-[17px]">
              Upload a different file
            </Button>
          </div>
        </div>
      </CreateHeader>
    );
  }

  return (
    <CreateHeader step={3} title="Building your track" blurb="This takes a few minutes. It shows as Ready in your library the moment it is done.">
      <div className="flex items-baseline justify-between gap-5">
        <div className="text-[19px] font-semibold tracking-[-0.02em]">{name}</div>
        <div className="font-mono text-[22px] tabular-nums text-acc">{percent}%</div>
      </div>
      <div className="relative mt-[14px] h-[3px] rounded-sm bg-line" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
        <div
          className="absolute inset-y-0 left-0 rounded-sm bg-accFill transition-[width] duration-700 ease-out motion-reduce:transition-none"
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className="mt-[26px]">
        <CreatePipeline stages={stages} />
      </div>
      <p className="mt-[22px] text-sm text-mute">You can close this page. The track keeps building and turns Ready in your library.</p>
      <div className="mt-5 flex gap-[10px]">
        <Link to="/library/mine" className={buttonClassName({ variant: 'outline', className: 'px-[17px]' })}>
          Back to library
        </Link>
        <Link to="/create" className={buttonClassName({ variant: 'outline', quiet: true, className: 'px-[17px]' })}>
          Create another
        </Link>
      </div>
    </CreateHeader>
  );
}
